import { KeywordProductivityType } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/ContentRequests/ContentRequestBuilder';

// Enums
export enum ToneType {
  Friendly = 'Friendly',
  Luxurious = 'Luxurious',
  Humorous = 'Humorous',
  Encouraging = 'Encouraging',
  Enthusiastic = 'Enthusiastic',
  Confident = 'Confident'
}

export enum VoiceType {
  Informative = 'Informative',
  Persuasive = 'Persuasive',
  Sustainable = 'Sustainable',
  Healthy = 'Healthy',
  Playful = 'Playful',
  Supportive = 'Supportive'
}

export enum VoiceAndToneMatchType {
  FullMatch = 'FullMatch',
  ToneMismatch = 'ToneMismatch',
  VoiceMismatch = 'VoiceMismatch',
  FullMismatch = 'FullMismatch'
}

export enum ContentMetricType {
  ContentScore = 'ContentScore',
  ContentUtilizationScore = 'ContentUtilizationScore',
  ContentAccuracyScore = 'ContentAccuracyScore',
  KeywordPerformanceScore = 'KeywordPerformanceScore',
  KeywordUsageScore = 'KeywordUsageScore',
  KeywordProductivityScore = 'KeywordProductivityScore',
  KeywordPopularityScore = 'KeywordPopularityScore',
  VoiceMatchScore = 'VoiceMatchScore',
  ToneMatchScore = 'ToneMatchScore',
  VoiceAndToneMatchAnalysis = 'VoiceAndToneMatchAnalysis',
  KeywordOrganicTraffic = 'KeywordOrganicTraffic',
  KeywordSearchVolume = 'KeywordSearchVolume'
}

export enum CategoryType {
  Department = 'Department',
  Category = 'Category',
  Subcategory = 'Subcategory',
  Brand = 'Brand'
}

export interface TimePeriodRange {
  startWeek: number;
  endWeek: number;
}

// Requests
export interface BaseMetricsRequest {
  retailerId: number;
  stacklineSkus: string[];
  categoryIds: number[];
  brandIds: number[];
  subcategoryIds: number[];
  segmentIds: string[];
  businessUnits: string[];
  startWeekId: number;
  endWeekId: number;
  resultCount: number;
  sortField: string;
  keywordProductivityType: KeywordProductivityType | undefined;
  pageNumber?: number;
  pageSize?: number;
  offset?: number;
}

export interface GetScoreForContentMetricRequest extends BaseMetricsRequest {
  contentMetricType: ContentMetricType;
  voiceAndToneMatchTypes: VoiceAndToneMatchType[];
}

export interface GetScoreForContentMetricByCategoryTypeRequest extends BaseMetricsRequest {
  contentMetricType: ContentMetricType;
  categoryType: CategoryType;
}

export interface GetContentMetricsForProductRequest {
  retailerSku: string;
  retailerId: number;
  stacklineSku: string;
  weekId: number;
}

export interface VoiceAndToneMetricsRequest extends BaseMetricsRequest {
  voiceAndToneMatchTypes: VoiceAndToneMatchType[];
  voiceTypes: VoiceType[];
  toneTypes: ToneType[];
}

export interface GetWeightedKeywordsAndPhrasesForProductRequest {
  retailerSku: string;
  retailerId: number;
  weekId: number;
  contentMetricType: ContentMetricType.KeywordOrganicTraffic | ContentMetricType.KeywordSearchVolume;
}

export interface ContentUtilizationFilters {
  // Minimum required scores for content utilization metrics
  minimumRequiredTitleUtilizationScore?: number;
  minimumRequiredBulletPointsUtilizationScore?: number;
  minimumRequiredImageUtilizationScore?: number;
  minimumRequiredDescriptionUtilizationScore?: number;
  minimumRequiredVideoUtilizationScore?: number;
  minimumRequiredAPlusUtilizationScore?: number;
  minimumRequiredContentUtilizationScore?: number;

  // Content accuracy metric filters - true means include products with inaccuracies in this metric
  filterTitleInaccuracies: boolean;
  filterBulletPointsInaccuracies: boolean;
  filterImageInaccuracies: boolean;
  filterDescriptionInaccuracies: boolean;
  filterVideoInaccuracies: boolean;

  // Minimum required scores for keyword performance metrics
  minimumRequiredKeywordUsageScore?: number;
  minimumRequiredKeywordPopularityScore?: number;
  minimumRequiredKeywordProductivityScore?: number;
}

// Responses
export interface ContentMetricScoreByWeekRecord {
  weekId: number;
  contentMetricType: ContentMetricType;
  score: number;
  averageCountOfKeywords?: number;
}

export interface GetContentScoreSummaryResponse {
  scores: ContentSummaryScores;
}

export interface ContentScoreByWeekRecord {
  weekId: number;
  scores: ContentSummaryScores;
}

export interface VoiceAndToneMatchAnalysisRecord {
  stacklineSku: string;
  scores: VoiceAndToneScores;
}

export interface GetContentScoreExtendedSummaryByWeekResponse {
  records: ContentScoreByWeekRecord[];
}

type ProductCountByVoiceRecord = {
  type: VoiceType;
  count: number;
};

type ProductCountByToneRecord = {
  type: ToneType;
  count: number;
};

export type ProductCountByVoiceOrToneRecord = ProductCountByVoiceRecord | ProductCountByToneRecord;

export interface GetProductCountByVoiceTypeResponse {
  records: ProductCountByVoiceRecord[];
}

export interface GetProductCountByToneTypeResponse {
  records: ProductCountByToneRecord[];
}

export type ContentSummaryScores = {
  contentScore: number;
  contentUtilizationScore: number;
  contentAccuracyScore: number;
  keywordUsageScore: number;
  keywordProductivityScore: number;
  keywordPopularityScore: number;
  voiceMatchScore: number;
  toneMatchScore: number;
} & BaseContentScores;

export type VoiceAndToneScores = {
  currentDominantVoiceType: VoiceType;
  currentDominantToneType: ToneType;
  expectedVoice: VoiceType;
  voiceMatchScore: number;
  expectedTone: ToneType;
  toneMatchScore: number;
  voiceAndToneMatchType: VoiceAndToneMatchType;
} & BaseContentScores;

type BaseContentScoreKeys =
  | 'aPlusScore'
  | 'descriptionScore'
  | 'totalProducts'
  | 'featureBulletsScore'
  | 'imagesScore'
  | 'titleScore'
  | 'videosScore'
  | 'contentUtilizationScore'
  | 'productsWithLowAPlusScore'
  | 'productsWithLowDescriptionScore'
  | 'productsWithLowImageScore'
  | 'productsWithLowVideoScore'
  | 'productsWithLowTitleScore'
  | 'productsWithLowContentScore'
  | 'productsWithLowBulletPointsScore';

export type BaseContentScores = {
  [K in BaseContentScoreKeys]: number;
};

export type ContentUtilizationScores = {
  contentUtilizationScore: number;
} & BaseContentScores;

export type ContentAccuracyScores = {
  contentAccuracyScore: number;
  productsWithInaccurateBulletPoints: number;
  productsWithInaccurateContent: number;
  productsWithInaccurateVideo: number;
  productsWithInaccurateImage: number;
  productsWithInaccurateTitle: number;
  productsWithInaccurateDescription: number;
} & BaseContentScores;

export interface GetContentUtilizationSummaryResponse {
  contentUtilizationScores: ContentUtilizationScores;
}

export type KeywordPerformanceScores = {
  keywordUsageScore: number;
  keywordProductivityScore: number;
  keywordPopularityScore: number;
  averageCountOfKeywords: number;
  averageCountOfKeywordsGeneratingTraffic: number;
  averageVolumeOfSearchTraffic: number;
  averageReferenceKeywordPopularity: number;
  averageUnproductiveKeywordsCount: number;
  expectedKeywordsCount: number;
  productsWithLowKeywordUsageScore: number;
  productsWithLowKeywordPopularityScore: number;
  productsWithLowKeywordProductivityScore: number;
  totalProducts: number;
};

export type ContentScores =
  | ContentUtilizationScores
  | ContentAccuracyScores
  | ContentSummaryScores
  | VoiceAndToneScores;

export type LengthBasedMetrics = {
  entityLengthNumerator: number;
  maxCharactersDenominator: number;
};

type CountBasedMetrics = {
  actualCountNumerator: number;
  expectedCountDenominator: number;
};

type MetricVariant = LengthBasedMetrics | CountBasedMetrics;

export interface MetaDataMetrics {
  aPlusMetrics: CountBasedMetrics;
  contentMetrics: CountBasedMetrics;
  descriptionMetrics: LengthBasedMetrics;
  bulletPointsMetrics: LengthBasedMetrics;
  imageMetrics: CountBasedMetrics;
  videoMetrics: CountBasedMetrics;
  titleMetrics: MetricVariant;
}

export interface AccuracyMetaDataMetrics {
  aPlusMetrics: CountBasedMetrics;
  contentMetrics: CountBasedMetrics;
  descriptionMetrics: CountBasedMetrics;
  bulletPointsMetrics: CountBasedMetrics;
  imageMetrics: CountBasedMetrics;
  videoMetrics: CountBasedMetrics;
  titleMetrics: CountBasedMetrics;
}

export interface GetContentAccuracySummaryResponse {
  contentAccuracyScores: ContentAccuracyScores;
}

export interface GetKeywordPerformanceScoreSummaryResponse {
  scores: KeywordPerformanceScores;
}

type MatchScore = {
  value: number;
  prevValue: number;
  difference: number;
};

export type ContentMetricScoreByProductRecord = {
  stacklineSku: string;
  scores: ContentScores;
  toneMatchScore: MatchScore;
  voiceMatchScore: MatchScore;
};

export interface GetScoreForContentMetricByProductResponse {
  records: ContentMetricScoreByProductRecord[];
}

export interface GetScoreForContentMetricByWeekResponse {
  records: ContentMetricScoreByWeekRecord[];
}

export interface GetContentUtilizationDetailMetricsForProductResponse {
  records: ProductContentUtilizationRecord[];
}

export interface GetContentUtilizationSummaryMetricsForProductResponse {
  retailerId: number;
  weekId: number;
  stacklineSku: string;
  scores: ContentUtilizationScores;
}

export interface GetContentAccuracySummaryMetricsForProductResponse {
  retailerId: number;
  weekId: number;
  stacklineSku: string;
  scores: ContentAccuracyScores;
}

export interface GetContentAccuracyDetailMetricsForProductResponse {
  records: ProductContentAccuracyRecord[];
}

interface CategoryScore {
  id: number;
  score: number;
  contentMetricType: ContentMetricType;
  categoryType: CategoryType;
}

export interface GetScoreForContentMetricByCategoryTypeResponse {
  records: CategoryScore[];
}

export interface GetContentUtilizationScoreByProductResponse {
  data: any;
}

export interface GetContentAccuracyScoreSummaryResponse {
  data: any;
}

export interface GetContentAccuracyScoreByProductResponse {
  data: any;
}

export interface GetVoiceAndToneAnalysisByProductResponse {
  records: {
    stacklineSku: string;
    currentDominantVoiceType: string;
    currentDominantToneType: string;
    expectedVoice: string;
    voiceMatchScore: number;
    expectedTone: string;
    toneMatchScore: number;
    voiceAndToneMatchType: string;
  }[];
}

export interface GetVoiceAndToneCurrentAndTargetVectorsResponse {
  retailerId: number;
  weekId: number;
  retailerSku: string;
  stacklineSku: string;
  currentVoiceVector: number[];
  targetVoiceVector: number[];
  currentToneVector: number[];
  targetToneVector: number[];
  targetDominantVoice: VoiceType;
  currentDominantVoice: VoiceType;
  targetDominantTone: ToneType;
  currentDominantTone: ToneType;
  voiceMatchScore: number;
  toneMatchScore: number;
}

export interface GetWeightedKeywordsAndPhrasesForProductResponse {
  retailerId: number;
  retailerSku: string;
  isSuccess: boolean;
  message: string;
  weightedKeywordPhrases: {
    retailerId: number;
    retailerSku: string;
    isSuccess: boolean;
    message: string;
    weightedKeywordPhrases: {
      sections: WeightedKeywordsAndPhrasesSection[];
    };
  };
}

// Product Level Content Utilization and Accuracy
export enum EntityItemType {
  BulletPoint = 'BulletPoint',
  BulletPointRecord = 'BulletPointRecord',
  Title = 'Title',
  Description = 'Description',
  Image = 'Image',
  ImageRecord = 'ImageRecord',
  Video = 'Video',
  VideoRecord = 'VideoRecord',
  APlusContent = 'APlusContent'
}

export interface ImageMetadata {
  width: number;
  height: number;
  unit: string;
}

export interface VideoMetadata extends ImageMetadata {
  duration: number;
}

interface EntityItem {
  entityValue: string;
  entityMetadata: ImageMetadata | VideoMetadata | null;
}

interface ProductContentUtilizationMetric {
  expectedCount: number;
  actualCount: number;
  score: number;
}

export type ProductContentUtilizationRecord = EntityItem & {
  entityType: EntityItemType;
  metrics: ProductContentUtilizationMetric;
  children: ProductContentUtilizationRecord[] | null;
};

export interface ProductContentAccuracyRecord {
  entityType: EntityItemType;
  currentEntity: EntityItem;
  approvedEntity: EntityItem;
  isMatch: boolean;
}

export interface WeightedKeywordPhrase {
  phrase: string;
  groupId?: string;
}

interface WeightedKeyword {
  keyword: string;
  weight: number;
  subcat_level_weight: number;
  sku_level_weight: number;
}

export interface WeightedKeywordGroup {
  groupId: string;
  totalWeight: number;
  totalSubcatWeight: number;
  weights: WeightedKeyword[];
}

export interface WeightedKeywordsAndPhrasesSection {
  name: string;
  content: WeightedKeywordPhrase[];
  groups: WeightedKeywordGroup[];
}

// TODO: confirm with backend which fields are optional
export interface GetContentResponse {
  retailerId: number;
  weekId: number;
  retailerSku: string;
  stacklineSku: string;
  timestamp: number;
  bullets?: string[];
  title?: string;
  description?: string;
  imageUrls?: string[];
  videoUrls?: string[];
  aplusScore?: number;
}

export interface GetKeywordOpportunitiesRequest {
  retailerId: number;
  retailerSku: string;
  weekId: string;
}

export interface KeywordOpportunity {
  keyword: string;
  organic_traffic_opportunity: number;
  organic_traffic_earned: number;
  organic_traffic_share_earned: number;
  organic_traffic_unearned: number;
  is_keyword_used: boolean;
  is_branded: boolean;
}

export interface GetKeywordOpportunitiesResponse {
  isSuccess: boolean;
  data: KeywordOpportunity[];
}

export enum KeywordType {
  Branded = 'Branded',
  Unbranded = 'Unbranded'
}

export enum KeywordUsedStatus {
  Used = 'Used',
  NotUsed = 'Not used'
}

export interface BaseVariationsResponse {
  isSuccess: boolean;
}

export interface GetVariationsOverviewMetricsResponse extends BaseVariationsResponse {
  data: {
    singleSkuCount: number;
    variatedSkuCount: number;
    correctChildCount: number;
    incorrectChildCount: number;
    inaccurateParentPages: number;
  };
}

export interface GetVariationsMetricsRequest extends BaseMetricsRequest {
  requestType: string;
}

export interface VariationsMetricsByWeekRecord {
  weekId: number;
  childVariationRate: number;
  childVariationRateNumerator: number;
  childVariationRateDenominator: number;
  singleSkuCount: number;
  variatedSkuCount: number;
  childAccuracy: number;
  childAccuracyNumerator: number;
  childAccuracyDenominator: number;
  correctChildCount: number;
  incorrectChildCount: number;
  parentPageAccuracy: number;
  parentPageAccuracyNumerator: number;
  parentPageAccuracyDenominator: number;
  inaccurateParentCount: number;
  parentPageCount: number;
  pageDensity: number;
  pageDensityNumerator?: number;
  pageDensityDenominator?: number;
}

export interface GetVariationsMetricsResponse extends BaseVariationsResponse {
  data: {
    records: VariationsMetricsByWeekRecord[];
  };
}

export enum VariationsMetricsByCategoryRequestType {
  VariationAccuracyByCategory = 'variationAccuracyByCategory',
  SingleSkuCountByCategory = 'singleSkuCountByCategory',
  VariatedSkuCountByCategory = 'variatedSkuCountByCategory',
  ChildAccuracyByCategory = 'childAccuracyByCategory',
  CorrectChildCountByCategory = 'correctChildCountByCategory',
  IncorrectChildCountByCategory = 'incorrectChildCountByCategory',
  ParentPageAccuracyByCategory = 'parentPageAccuracyByCategory',
  InaccurateParentCountByCategory = 'inaccurateParentCountByCategory',
  PageDensityByCategory = 'pageDensityByCategory',
  ParentPageCountByCategory = 'parentPageCountByCategory'
}

export interface GetVariationsMetricsByCategoryRequest extends BaseMetricsRequest {
  requestType: VariationsMetricsByCategoryRequestType;
  categoryType: CategoryType;
}

export interface CategoryMetricsRecord {
  metricType: string;
  categoryType: string;
  entityId: number;
  metricValue: number;
  pageDensity: number;
}

export interface GetVariationsMetricsByCategoryResponse extends BaseVariationsResponse {
  data: {
    records: CategoryMetricsRecord[];
  };
}

export interface VariationsByCategoryData {
  mainRecords: GetVariationsMetricsByCategoryResponse['data']['records'];
  comparisonRecords: GetVariationsMetricsByCategoryResponse['data']['records'];
}

export interface PageSizeRecord {
  parentSku: string;
  pageSize: number;
}

export interface GetVariationsPageSizeResponse extends BaseVariationsResponse {
  data: {
    records: PageSizeRecord[];
  };
}

export enum VariationsDetailGridMetricType {
  ChildDetailGrid = 'childDetailGrid',
  PageDetailGrid = 'pageDetailGrid'
}
interface ChildDetailGridRecord {
  metricType: VariationsDetailGridMetricType.ChildDetailGrid;
  parentSku: string;
  expectedParent: string;
  stacklineSku: string;
  retailSales: string;
  inventory: string;
}

interface PageDetailGridRecord {
  metricType: VariationsDetailGridMetricType.PageDetailGrid;
  parentSku: string;
  stacklineSku: string;
  retailSales: string;
  childSkuCount: number;
  salesPerSku: string;
}

export type VariationsProductDetailsRecord = ChildDetailGridRecord | PageDetailGridRecord;

export interface GetVariationsProductDetailsGridResponse extends BaseVariationsResponse {
  data: {
    records: VariationsProductDetailsRecord[];
  };
}
